import { backendApiClient } from '@geneo2-web/services-clients';
import { PAL_BASE_URL } from '@geneo2-web/shared-ui';
import { AxiosRequestConfig } from 'axios';
import type { IPerformanceSummary } from './learners';
import {
  AnswerHintUsedEnum,
  AnswerKnowledgeUpdates,
  AnswerModeEnum,
  SubmissionDetails,
} from './common';

const CHALLENGE_START_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/challenge/sessions/${session_id}/challenge/start`;

const CHALLENGE_ANSWER_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/challenge/sessions/${session_id}/challenge/answer`;

const GET_CHALLENGE_PATH = (session_id: string) =>
  `${PAL_BASE_URL || ''}/adaptive/challenge/sessions/${session_id}/challenge`;

const CHALLENGE_ABANDON_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/challenge/sessions/${session_id}/challenge/abandon`;

export interface IChallengeQuestion {
  question_id: string;
  difficulty: string;
  lo_ids: string[];
  order: number;
}

export interface IChallengeProgress {
  questions_answered: number;
  total_questions: number;
  correct_count: number;
  current_accuracy: number;
  los_covered: number;
  total_los: number;
  lo_coverage_complete?: boolean;
  is_complete: boolean;
  result: string | null;
  result_reason?: string | null;
  summary_text?: string | null;
}

export interface IChallengeSessionSnapshot {
  session_id: string;
  concept_id: string;
  concept_name: string;
  mode: string;
  questions: IChallengeQuestion[];
  progress: IChallengeProgress;
  next_question?: IChallengeQuestion;
  attempt_number: number;
  accuracy_threshold: number;
}

export interface IChallengeStartResponse {
  session_id: string;
  concept_id: string;
  concept_name: string;
  questions: IChallengeQuestion[];
  total_questions: number;
  attempt_number: number;
  accuracy_threshold: number;
  started_at: string;
}

export interface IChallengeAnswerScore {
  rubric_type: string;
  value: number;
}

export interface IChallengeAnswerRequest {
  question_id: string;
  answer: string[];
  time_taken_seconds: number;
  answer_mode: AnswerModeEnum;
  hint_used?: AnswerHintUsedEnum;
  primary_concept_id?: string;
}

export interface IChallengeAnswerResponse
  extends SubmissionDetails, AnswerKnowledgeUpdates {
  event_id: string;
  session_id: string;
  correct: boolean;
  score: number;
  challenge_progress: IChallengeProgress;
  next_question?: IChallengeQuestion;
}

export async function startChallengeApi(
  session_id: string,
  body: Record<string, never> = {},
  options: AxiosRequestConfig = {}
): Promise<IChallengeStartResponse> {
  return await backendApiClient.post<IChallengeStartResponse>(
    CHALLENGE_START_PATH(session_id),
    body,
    options
  );
}

export async function submitChallengeAnswerApi(
  session_id: string,
  body: IChallengeAnswerRequest,
  options: AxiosRequestConfig = {}
): Promise<IChallengeAnswerResponse> {
  return await backendApiClient.post<IChallengeAnswerResponse>(
    CHALLENGE_ANSWER_PATH(session_id),
    body,
    options
  );
}

export async function getChallengeApi(
  session_id: string,
  options: AxiosRequestConfig = {}
): Promise<IChallengeSessionSnapshot> {
  return await backendApiClient.get<IChallengeSessionSnapshot>(
    GET_CHALLENGE_PATH(session_id),
    options
  );
}

export async function abandonChallengeApi(
  session_id: string,
  body: Record<string, never> = {},
  options: AxiosRequestConfig = {}
): Promise<IChallengeSessionSnapshot> {
  return await backendApiClient.post<IChallengeSessionSnapshot>(
    CHALLENGE_ABANDON_PATH(session_id),
    body,
    options
  );
}

const CHALLENGE_RESULTS_PATH = (session_id: string) =>
  `${
    PAL_BASE_URL || ''
  }/adaptive/challenge/sessions/${session_id}/challenge/results`;

export interface IChallengeResultData {
  summary_text?: string;
}

export interface IDiagnosticResultsResponse {
  results: IPerformanceSummary;
  data?: IChallengeResultData;
}

export async function getChallengeResultsApi(
  session_id: string,
  module_id: string,
  concept_id: string,
  options: AxiosRequestConfig = {}
): Promise<IDiagnosticResultsResponse> {
  return await backendApiClient.get<IDiagnosticResultsResponse>(
    CHALLENGE_RESULTS_PATH(session_id),
    { ...options, params: { module_id, concept_id, ...options.params } }
  );
}
